import { useEffect, useRef, useState } from "react";

/**
 * Tracks user activity (mouse / keyboard / scroll / touch / visibility)
 * and emits a derived idle status. Used by `<SessionGuard>` to drive the
 * session-expiry warning modal.
 *
 * Activity events are listener-attached on `window` while `enabled` is
 * true, and a 1s `setInterval` recomputes the derived status. When
 * `enabled` is false (e.g. user is signed out) the listeners and timer
 * are torn down so this hook costs nothing on the login page.
 */

const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
] as const;

export type IdleStatus = "active" | "warning" | "expired";

export interface IdleTimerOptions {
  /** Total ms of inactivity allowed before status flips to "expired". */
  idleLimitMs: number;
  /** ms before `idleLimitMs` at which status flips to "warning". */
  warningLeadMs: number;
  /** Gate the timer — pass `auth.isAuthenticated` here. */
  enabled: boolean;
}

export interface IdleTimerState {
  /** ms since the last detected activity event. */
  idleMs: number;
  status: IdleStatus;
  /** Manually mark the user as active now (e.g., after "Stay signed in"). */
  reset: () => void;
}

export function useIdleTimer({
  idleLimitMs,
  warningLeadMs,
  enabled,
}: IdleTimerOptions): IdleTimerState {
  const lastActivityRef = useRef<number>(Date.now());
  const [idleMs, setIdleMs] = useState(0);
  const [status, setStatus] = useState<IdleStatus>("active");

  useEffect(() => {
    if (!enabled) {
      // Reset state so the next "enabled" cycle starts clean.
      lastActivityRef.current = Date.now();
      setIdleMs(0);
      setStatus("active");
      return;
    }

    const markActive = () => {
      lastActivityRef.current = Date.now();
    };

    // visibilitychange: when the tab regains focus we don't necessarily
    // count the time-away as "user activity" — that would defeat the
    // purpose of HIPAA-driven idle. Instead we just immediately
    // recompute the derived status, so a returning user who's been over
    // the limit sees the modal right away.
    const onVisibility = () => {
      recompute();
    };

    const recompute = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      setIdleMs(elapsed);
      const warningStart = idleLimitMs - warningLeadMs;
      const next: IdleStatus =
        elapsed >= idleLimitMs
          ? "expired"
          : elapsed >= warningStart
            ? "warning"
            : "active";
      setStatus(next);
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, markActive, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisibility);

    const interval = window.setInterval(recompute, 1000);
    recompute();

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, markActive);
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [enabled, idleLimitMs, warningLeadMs]);

  const reset = () => {
    lastActivityRef.current = Date.now();
    setIdleMs(0);
    setStatus("active");
  };

  return { idleMs, status, reset };
}
